import React from 'react';
import {
    Filter, RotateCcw, Layers, Radio, BookOpen, Shield
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    EMPTY_WORKFORCE_FILTERS,
    hasActiveWorkforceFilters,
} from './workforceFilters';
import './WorkforceFilterSidebar.css';

const STATUS_OPTIONS = [
    { id: 'live', label: 'Live' },
    { id: 'draft', label: 'Draft' },
];

const OPCODE_OPTIONS = [
    { id: '0', label: 'No opcodes' },
    { id: '1', label: '1 opcode' },
    { id: '2-3', label: '2–3 opcodes' },
    { id: '4+', label: '4+ opcodes' },
];

const CHANNEL_OPTIONS = [
    { id: 'has', label: 'Has channels' },
    { id: 'none', label: 'No channels' },
];

const KNOWLEDGE_OPTIONS = [
    { id: 'has', label: 'With knowledge' },
    { id: 'none', label: 'No knowledge' },
];

function toggleValue(list, id) {
    return list.includes(id) ? list.filter((v) => v !== id) : [...list, id];
}

function FilterSectionSkeleton({ optionCount = 2 }) {
    return (
        <section className="wf-filter-section" aria-hidden="true">
            <div className="wf-filter-skeleton-title animate-pulse" />
            <ul className="wf-filter-options">
                {Array.from({ length: optionCount }, (_, i) => (
                    <li key={i} className="wf-filter-skeleton-option">
                        <div className="wf-filter-skeleton-checkbox animate-pulse" />
                        <div
                            className="wf-filter-skeleton-label animate-pulse"
                            style={{ width: `${58 + (i % 3) * 14}%` }}
                        />
                        <div className="wf-filter-skeleton-count animate-pulse" />
                    </li>
                ))}
            </ul>
        </section>
    );
}

function FilterSidebarSkeleton() {
    return (
        <>
            <div className="wf-filter-skeleton-summary animate-pulse" aria-hidden="true" />
            <FilterSectionSkeleton optionCount={2} />
            <FilterSectionSkeleton optionCount={3} />
            <FilterSectionSkeleton optionCount={4} />
            <FilterSectionSkeleton optionCount={2} />
            <FilterSectionSkeleton optionCount={2} />
        </>
    );
}

function FilterSection({
    title, icon: Icon, options, selected, counts, onToggle
}) {
    return (
        <section className="wf-filter-section">
            <h3 className="wf-filter-section-title">
                {Icon ? <Icon size={13} aria-hidden /> : null}
                {title}
            </h3>
            <ul className="wf-filter-options">
                {options.map((opt) => {
                    const count = counts?.[opt.id] ?? opt.count ?? 0;
                    const checked = selected.includes(opt.id);
                    return (
                        <li key={opt.id}>
                            <label className={cn('wf-filter-option', checked && 'wf-filter-option--active')}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => onToggle(opt.id)}
                                />
                                <span className="wf-filter-option-label" title={opt.label}>{opt.label}</span>
                                <span className="wf-filter-option-count">{count}</span>
                            </label>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

export default function WorkforceFilterSidebar({
    filters,
    onChange,
    facetCounts,
    filteredCount,
    totalCount,
    loading = false,
}) {
    const active = hasActiveWorkforceFilters(filters);

    function setKey(key, id) {
        onChange({
            ...filters,
            [key]: toggleValue(filters[key] || [], id),
        });
    }

    function clearAll() {
        onChange({ ...EMPTY_WORKFORCE_FILTERS });
    }

    return (
        <aside className="wf-filter-sidebar" aria-label="Filter assistants" aria-busy={loading}>
            <div className="wf-filter-sidebar-head">
                <div className="wf-filter-sidebar-title">
                    <Filter size={14} aria-hidden />
                    <span>Filters</span>
                </div>
                {active && (
                    <button type="button" className="wf-filter-clear" onClick={clearAll}>
                        <RotateCcw size={12} aria-hidden />
                        Clear
                    </button>
                )}
            </div>

            {loading ? (
                <FilterSidebarSkeleton />
            ) : (
                <>
                    <p className="wf-filter-summary">
                        Showing <b>{filteredCount}</b> of {totalCount}
                    </p>

                    <FilterSection
                        title="Status"
                        icon={Shield}
                        options={STATUS_OPTIONS}
                        selected={filters.status}
                        counts={facetCounts?.status}
                        onToggle={(id) => setKey('status', id)}
                    />

                    <FilterSection
                        title="OpCodes"
                        icon={Layers}
                        options={OPCODE_OPTIONS}
                        selected={filters.opcodeCount}
                        counts={facetCounts?.opcodeCount}
                        onToggle={(id) => setKey('opcodeCount', id)}
                    />

                    <FilterSection
                        title="Channels"
                        icon={Radio}
                        options={CHANNEL_OPTIONS}
                        selected={filters.channels}
                        counts={facetCounts?.channels}
                        onToggle={(id) => setKey('channels', id)}
                    />

                    <FilterSection
                        title="Knowledge"
                        icon={BookOpen}
                        options={KNOWLEDGE_OPTIONS}
                        selected={filters.knowledge}
                        counts={facetCounts?.knowledge}
                        onToggle={(id) => setKey('knowledge', id)}
                    />
                </>
            )}
        </aside>
    );
}
